import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getSettings } from "src/services/configService";

import { Box, Typography } from "@mui/material";
import { DangerButton, PrimaryButton } from "@shared/CustomButton";
import { LoadingBox } from "@shared/Loading";
import { Modal } from "@shared/Modal/Modal";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { useUpdateWorkspaceSettingMutation } from "../../api";
import { useCurrentWorkspace } from "../../api/helpers";

import { useStyles } from "./WorkspaceConfigurationPanel.style";
import {
    buildWorkspaceSettingsState,
    getVisibleWorkspacePages,
    getWorkspaceSettingLabelKey,
    workspaceGlobalSettingNames,
} from "./WorkspaceConfigurationPanel.utils";
import { DefaultGroupRow } from "./WorkspaceDefaultGroupRow";
import { WorkspacePageSettingCard } from "./WorkspacePageSettingCard";
import { WorkspaceSettingRow } from "./WorkspaceSettingRow";

export const WorkspaceConfigurationPanel = () => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common", "sidepanels"]);

    const { data: workspace, isLoading: isWorkspaceLoading } = useCurrentWorkspace();
    const workspaceId = workspace?.id;

    const valueByName = useMemo(
        () => (workspace ? buildWorkspaceSettingsState(workspace.settings ?? []).valueByName : null),
        [workspace],
    );

    const pendingRef = useRef(new Set<API.WorkspaceSettingName>());
    const [pendingNames, setPendingNames] = useState(() => new Set<API.WorkspaceSettingName>());
    const [twoFactorDialog, setTwoFactorDialog] = useState<{ nextChecked: boolean } | null>(null);

    const [updateSetting] = useUpdateWorkspaceSettingMutation();

    const submitWorkspaceSetting = async (name: API.WorkspaceSettingName, checked: boolean) => {
        if (!workspaceId || pendingRef.current.has(name)) {
            return;
        }
        pendingRef.current.add(name);
        setPendingNames(new Set(pendingRef.current));
        try {
            await updateSetting({ id: workspaceId, name, value: checked ? 1 : 0 }).unwrap();
            showSuccessToast(t("pages:workspacePage.toast.workspaceSettingUpdated"));
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("pages:workspacePage.toast.workspaceSettingUpdateFailed"));
        } finally {
            pendingRef.current.delete(name);
            setPendingNames(new Set(pendingRef.current));
        }
    };

    const handleToggle = (name: API.WorkspaceSettingName, checked: boolean) => {
        if (name === "MembersRequireTwoFactorAuthentication") {
            setTwoFactorDialog({ nextChecked: checked });
            return;
        }
        void submitWorkspaceSetting(name, checked);
    };

    const confirmTwoFactorChange = () => {
        if (!twoFactorDialog) {
            return;
        }
        const { nextChecked } = twoFactorDialog;
        setTwoFactorDialog(null);
        void submitWorkspaceSetting("MembersRequireTwoFactorAuthentication", nextChecked);
    };

    const rowIsBusy = (name: API.WorkspaceSettingName) =>
        pendingNames.has(name) || (name === "MembersRequireTwoFactorAuthentication" && twoFactorDialog !== null);

    const showLoading = isWorkspaceLoading && workspace === null;

    const { features } = getSettings();
    const visiblePages = getVisibleWorkspacePages(features);

    return (
        <>
            {showLoading || valueByName === null ? (
                <LoadingBox sx={{ marginY: 4 }} />
            ) : (
                <Box className={classes.stack}>
                    <Box className={classes.settingList}>
                        {visiblePages.map((page) => (
                            <WorkspacePageSettingCard
                                key={page.name}
                                page={page}
                                valueByName={valueByName}
                                isRowBusy={rowIsBusy}
                                onToggle={handleToggle}
                            >
                                {page.name === "GroupsPageEnabled" && workspace ? (
                                    <DefaultGroupRow workspace={workspace} />
                                ) : null}
                            </WorkspacePageSettingCard>
                        ))}
                    </Box>

                    {workspaceGlobalSettingNames.length > 0 ? (
                        <SectionPanel title={t("pages:workspacePage.configuration.groups.workspaceWide")}>
                            {workspaceGlobalSettingNames.map((name) => (
                                <WorkspaceSettingRow
                                    key={name}
                                    name={name}
                                    label={t(getWorkspaceSettingLabelKey(name))}
                                    value={valueByName[name]}
                                    isBusy={rowIsBusy(name)}
                                    onToggle={handleToggle}
                                />
                            ))}
                        </SectionPanel>
                    ) : null}
                </Box>
            )}

            {twoFactorDialog !== null ? (
                <Modal
                    open
                    title={
                        twoFactorDialog.nextChecked
                            ? t("pages:workspacePage.configuration.twoFactorConfirm.titleEnable")
                            : t("pages:workspacePage.configuration.twoFactorConfirm.titleDisable")
                    }
                    onClose={() => setTwoFactorDialog(null)}
                    secondaryActionText={t("common:button.cancel")}
                    endButton={
                        twoFactorDialog.nextChecked ? (
                            <PrimaryButton onClick={confirmTwoFactorChange}>
                                {t("pages:workspacePage.configuration.twoFactorConfirm.confirmEnable")}
                            </PrimaryButton>
                        ) : (
                            <DangerButton onClick={confirmTwoFactorChange}>
                                {t("pages:workspacePage.configuration.twoFactorConfirm.confirmDisable")}
                            </DangerButton>
                        )
                    }
                >
                    <Typography color="text.primary" variant="body1" component="p">
                        {twoFactorDialog.nextChecked
                            ? t("pages:workspacePage.configuration.twoFactorConfirm.descriptionEnable")
                            : t("pages:workspacePage.configuration.twoFactorConfirm.descriptionDisable")}
                    </Typography>
                </Modal>
            ) : null}
        </>
    );
};
